import React, { useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function PerfTemplateTab() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    api.get('/perf/template').then(r => { if (!ignore) setQuestions(r.data.questions); }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, []);

  const upd = (i, patch) => setQuestions(qs => qs.map((q, j) => j === i ? { ...q, ...patch } : q));
  const add = () => setQuestions(qs => [...qs, { id: `n${Date.now()}`, category: '', text: '', type: 'rating' }]);
  const remove = (i) => setQuestions(qs => qs.filter((_, j) => j !== i));

  const save = async () => {
    if (questions.some(q => !q.text.trim())) { toast.error('Every question needs text — fill in or remove empty rows'); return; }
    setSaving(true);
    try {
      const { data } = await api.put('/perf/template', { questions });
      setQuestions(data.questions);
      toast.success('Template saved — new reviews will use these questions');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0]" /></div>;

  return (
    <div className="space-y-3 max-w-4xl" data-testid="perf-template-tab">
      <p className="text-xs text-[#8E85A0]">Edits apply to reviews you open from now on — reviews already in progress keep their original questions.</p>
      <div className="space-y-2">
        {questions.map((q, i) => (
          <div key={q.id} className="flex flex-col sm:flex-row gap-2 rounded-lg border border-[#E8E2F2] bg-white p-2.5" data-testid={`tmpl-q-${q.id}`}>
            <Input className="sm:w-40" value={q.category} placeholder="Category"
                   onChange={e => upd(i, { category: e.target.value })} data-testid={`tmpl-cat-${q.id}`} />
            <Input className="flex-1" value={q.text} placeholder="Question text"
                   onChange={e => upd(i, { text: e.target.value })} data-testid={`tmpl-text-${q.id}`} />
            <select className="rounded-md border border-[#DED8EB] p-2 text-xs" value={q.type}
                    onChange={e => upd(i, { type: e.target.value })} data-testid={`tmpl-type-${q.id}`}>
              <option value="rating">1–5 rating</option>
              <option value="text">Written answer</option>
            </select>
            <Button variant="ghost" size="icon" onClick={() => remove(i)} className="text-[#D9534F]" data-testid={`tmpl-del-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={add} data-testid="tmpl-add-btn"><Plus className="h-4 w-4 mr-1" /> Add question</Button>
        <Button onClick={save} disabled={saving} className="bg-[#6B3FA0]" data-testid="tmpl-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save template
        </Button>
      </div>
    </div>
  );
}
